import { debounceTime } from 'rxjs/operators';
import { PinCodeService } from 'src/app/services/pin-code/pin-code.service';
import { Injectable } from '@angular/core';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';
import { cordova, IonicNativePlugin } from '@ionic-native/core';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { filter, Subject } from 'rxjs';
import { ObjectUrlService } from './../object-url/object-url.service';
import { Html5CanvasPage } from 'src/app/modals/html5-canvas/html5-canvas.page';
import { Question, QuestionTypes } from 'src/app/models/question.model';
import { AlertService } from '../alert/alert.service';
import { File } from '../questionnaire/questionnaire.service';
import { File as FilePlugin, FileEntry } from '@ionic-native/file/ngx';
import { FileUploadComponent } from './../../modals/file-upload/file-upload.component';
import { TENANT_NAME, VIDEO_RECORDING_QUALITY } from '../../../config';
import { stopMediaStreamTracks } from 'src/app/utilities/camera';
import { EventBusService, EventTopic } from '../event-bus/event-bus.service';
import { ViewInstructionsPage } from 'src/app/modals/view-instructions/view-instructions.page';
import { blobToFile } from 'src/app/utilities/file';
import { v4 as uuidv4 } from 'uuid';

export enum CameraView {
  FrontView = 'use_front',
  BackView = 'use_back',
  AllowChanging = 'allow_changing',
}

export type retakeType = 'reupload' | 'retake';
export const SET_AUTO_LOCK_TRUE = 'set_auto_lock_true'; // set auto lock back to true on platform.resume

@Injectable({
  providedIn: 'root',
})
export class PhotoVideoService extends IonicNativePlugin {
  static pluginName = 'videorecorder';
  static plugin = 'cordova-plugin-videorecording';
  static pluginRef = 'VideoRecorder';
  static repo = 'https://github.com/Curve-Tomorrow/VideoRecorderPlugin.git';
  static platforms = ['iOS'];

  isMediaUploaded = false;
  mediaData: string;
  unverifiedQuestionIds: number[] = [];
  isCameraOpening = false;
  videoPhotoResultChanged = new Subject<Question>();
  startRecordingSubject = new Subject<Question>();
  setAutoLock$ = new Subject<boolean>();

  private currentIncludedQuestions: Question[] = [];

  constructor(
    public platform: Platform,
    private androidPermissions: AndroidPermissions,
    private modalController: ModalController,
    private alertSvc: AlertService,
    private translate: TranslateService,
    private filePlugin: FilePlugin,
    private objectURLsvc: ObjectUrlService,
    private eventBusSvc: EventBusService,
    private alertController: AlertController,
    private pinCodeSvc: PinCodeService
  ) {
    super();
    this.eventBusSvc.addSubscriber(
      EventTopic.CurrentIncludedQuestionsUpdated,
      (questions: Question[]) => {
        this.currentIncludedQuestions = questions;
      },
      PhotoVideoService.name
    );
    platform.resume.pipe(filter(() => this.isCameraOpening)).subscribe(async () => {
      this.isCameraOpening = false;
      this.setAutoLock$.next(true);
    });

    this.setAutoLock$.pipe(debounceTime(500)).subscribe(isAutoLock => {
      this.pinCodeSvc.setAutoLock(isAutoLock);
    });
  }

  cordova = cordova;

  get translations() {
    return {
      cameraAccessDenied: this.translate.instant('PhotoVideoPlugin.cameraAccessDenied'),
      microphoneAccessDenied: this.translate.instant('PhotoVideoPlugin.microphoneAccessDenied'),
      changeInAppSetting: this.translate.instant('PhotoVideoPlugin.changeInAppSetting'),
      pleaseAllowCamera: this.translate.instant('PhotoVideoPlugin.pleaseAllowCamera'),
      pleaseAllowMicrophone: this.translate.instant('PhotoVideoPlugin.pleaseAllowMicrophone'),
      pleaseConfirm: this.translate.instant('PhotoVideoPlugin.pleaseConfirm'),
      cancelCapture: this.translate.instant('PhotoVideoPlugin.cancelCapture'),
      cancel: this.translate.instant('CommonTexts.cancel'),
      settings: this.translate.instant('PhotoVideoPlugin.settings'),
      pleaseWait: this.translate.instant('PhotoVideoPlugin.pleaseWait'),
      yes: this.translate.instant('CommonTexts.yes'),
      no: this.translate.instant('CommonTexts.no'),
      shortRecordingHeader: this.translate.instant('Html5CanvasPage.shortRecordingHeader'),
      shortRecordingText: this.translate.instant('Html5CanvasPage.shortRecordingText'),
      recordingNotCompletedText: this.translate.instant('Html5CanvasPage.recordingNotCompletedText'),
      shortRecordingRetry: this.translate.instant('Html5CanvasPage.shortRecordingRetry'),
      cancelVideoText: this.translate.instant('PhotoVideoPlugin.cancelVideoText'),
      recordingNotCompleted: this.translate.instant('PhotoVideoPlugin.recordingNotCompleted'),
      shortRecordingCancel: this.translate.instant('PhotoVideoPlugin.shortRecordingCancel'),
    };
  }

  startRecording(question: Question): Promise<any> {
    return this.cordova(this, 'startRecording', {}, [
      {
        cameraData: question,
        appName: TENANT_NAME,
        translations: this.translations,
        videoQuality: VIDEO_RECORDING_QUALITY,
      },
    ]);
  }
  openCamera(question: Question): Promise<any> {
    return question.type === QuestionTypes.PhotoQuestion
      ? this.cordova(this, 'captureImage', {}, [{ cameraData: question, appName: TENANT_NAME, translations: this.translations }])
      : this.cordova(this, 'recordVideo', {}, [{ cameraData: question, appName: TENANT_NAME, translations: this.translations }]);
  }

  async openCameraPlugin(question: Question) {
    if (this.isCameraOpening) {
      return;
    }
    this.isCameraOpening = true;
    this.toggleVerifyQuestion(question.id, false);

    if (this.platform.is('cordova') && this.platform.is('ios')) {
      this.recordingOnIos(question);
    } else if (this.platform.is('cordova') && this.platform.is('android')) {
      this.recordingOnAndroid(question);
    } else {
      this.recordingOnNonCordova(question);
    }

    if (this.platform.is('cordova') && (this.platform.is('ios') || this.platform.is('android'))) {
      // workaround when permission is getting denied, so the button will be back to enabled
      setTimeout(() => {
        this.isCameraOpening = false;
        localStorage.setItem(SET_AUTO_LOCK_TRUE, '1');
      }, 2000);
    }
  }

  recordingOnIos(question: Question) {
    this.pinCodeSvc.setAutoLock(false);
    this.startRecording(question)
      .then(async data => {
        this.isCameraOpening = false;
        this.isMediaUploaded = true;
        this.setAutoLock$.next(true);
        if (question.type === 'VideoQuestion') {
          const name = 'video.webm';
          const type = 'video/webm';
          const file: File = { name, type, fullPath: '' };
          await this.moveFile(data.video).then(path => {
            file.fullPath = path;
            file.logo = data.logo;
          });
          question.videoPhotoResult = file;
          question.manualVideoPhotoUpload = false;
          this.videoPhotoResultChanged.next(question);

          this.setMediaObject(question);
        } else {
          const name = 'captured_photo.png';
          const type = 'image/jpeg';
          const file: File = { name, type, fullPath: '' };
          await this.moveFile(data.video).then(path => {
            file.fullPath = path;
          });
          question.videoPhotoResult = file;
          question.manualVideoPhotoUpload = false;
          this.videoPhotoResultChanged.next(question);
          this.setMediaObject(question);
        }
      })
      .catch(error => {
        this.setAutoLock$.next(true);
        this.isCameraOpening = false;
        console.error(error);
      });
  }

  recordingOnAndroid(question: Question) {
    this.pinCodeSvc.setAutoLock(false);
    if (question.type === 'VideoQuestion') {
      this.androidPermissions
        .requestPermissions([
          this.androidPermissions.PERMISSION.CAMERA,
          this.androidPermissions.PERMISSION.MODIFY_AUDIO_SETTINGS,
          this.androidPermissions.PERMISSION.RECORD_AUDIO,
        ])
        .then(camera => {
          if (camera.hasPermission) {
            this.openCamera(question).then(async data => {
              this.setAutoLock$.next(true);
              this.isMediaUploaded = true;
              const name = 'captured_video.mp4';
              const type = 'video/mp4';
              if (data.video && !data.video.startsWith('file://')) {
                data.video = 'file://' + data.video;
              }
              const file: File = { name, type, fullPath: data.video, logo: data.thumbnail };
              question.videoPhotoResult = file;
              question.manualVideoPhotoUpload = false;
              this.videoPhotoResultChanged.next(question);
              this.setMediaObject(question);
            });
          } else {
            this.showPermissionDeniedAlert();
          }
        });
    } else {
      this.androidPermissions.requestPermissions([this.androidPermissions.PERMISSION.CAMERA]).then(camera => {
        if (camera.hasPermission) {
          this.openCamera(question).then(async data => {
            this.setAutoLock$.next(true);
            this.isMediaUploaded = true;
            const name = 'captured_photo.png';
            const type = 'image/jpeg';
            if (data.image && !data.image.startsWith('file://')) {
              data.image = 'file://' + data.image;
            }
            const file: File = { name, type, fullPath: data.image };
            question.videoPhotoResult = file;
            question.manualVideoPhotoUpload = false;
            this.videoPhotoResultChanged.next(question);
            this.setMediaObject(question);
          });
        } else {
          this.showPermissionDeniedAlert();
        }
      });
    }
  }

  async recordingOnNonCordova(question: Question) {
    try {
      // iphone & ipad cant record a big video on safari
      // so fallback to file upload instead
      // https://github.com/muaz-khan/RecordRTC/issues/782#issuecomment-986439163
      if (question.type === QuestionTypes.VideoQuestion && this.isIosBrowser()) {
        throw new Error('not supported browser/device');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stopMediaStreamTracks(stream);
      if (this.currentIncludedQuestions.find(q => q.id === question.id)) {
        this.showModal(question);
      } else {
        this.isCameraOpening = false;
        this.setAutoLock$.next(true);
      }
    } catch (error) {
      this.isCameraOpening = false;
      this.setAutoLock$.next(true);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.showCameraPermissionDenyAlert(
          this.translate.instant('PhotoVideoPage.permissionDenyMessageA') +
            ' ' +
            TENANT_NAME +
            ' ' +
            this.translate.instant('PhotoVideoPage.permissionDenyMessageBrowser')
        );
      } else {
        this.showUploadModal(question, UploadModalType.fallback);
      }
    }
  }

  showCameraPermissionDenyAlert(message: string) {
    this.alertSvc.presentAlert(
      this.translate.instant('PhotoVideoPage.permissionDenyTitle'),
      '',
      message,
      this.translate.instant('QuestionTooltipModal.ok'),
      'camera-alert-ctrl'
    );
  }

  async showModal(question) {
    const modal = await this.modalController.create({
      component: Html5CanvasPage,
      componentProps: {
        question,
      },
      cssClass: 'html5-canvas-modal',
      swipeToClose: true,
      backdropDismiss: true,
    });
    modal.onWillDismiss().then(
      result => {
        if (result && result.data && result.data.error && result.data.error.name) {
          this.showUploadModal(question, UploadModalType.fallback);
        }
        this.isCameraOpening = false;
      },
      () => {
        this.isCameraOpening = false;
        this.setAutoLock$.next(true);
      }
    );
    return await modal.present();
  }

  async showUploadModal(question: Question, type: UploadModalType) {
    const titleKey = question.type === QuestionTypes.VideoQuestion ? 'uploadYourVideo' : 'uploadYourPhoto';
    let title = this.translate.instant('PhotoVideoPage.' + titleKey);
    let subtitle = '';

    if (type === UploadModalType.fallback) {
      title = this.translate.instant('FileUpload.' + (this.isIosBrowser() ? 'failedToOpenCameraInBrowser' : 'failedToOpenCamera'));
      const subtitleKey = question.isVideo
        ? this.isIosBrowser()
          ? 'browserNotSupportVideoClickUnderneath'
          : 'browserNotSupportVideo'
        : 'browserNotSupportPhoto';
      subtitle = this.translate.instant('FileUpload.' + subtitleKey);
    }

    const modal = await this.modalController.create({
      component: FileUploadComponent,
      componentProps: {
        title,
        subtitle,
        tapToUploadText: this.translate.instant(
          'FileUpload.' + (this.isIosBrowser() && question.isVideo ? 'tapToUploadOrTakeVideo' : 'tapToUpload')
        ),
        fileType: question.type === QuestionTypes.VideoQuestion ? 'video' : 'image',
        maxFileSizeMb: question.type === QuestionTypes.VideoQuestion ? 1000 : 50,
      },
      cssClass: 'wg-modal',
      swipeToClose: false,
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((data: { data: { file: File } }) => {
      if (data && data.data && data.data.file) {
        question.videoPhotoResult = data.data.file;
        question.manualVideoPhotoUpload = true;
        this.videoPhotoResultChanged.next(question);
        this.isMediaUploaded = true;
        this.setMediaObject(question);
      }
    });

    return await modal.present();
  }

  setMediaObject(question: Question) {
    if (question.videoPhotoResult) {
      this.mediaData = question.videoPhotoResult.fullPath;
    } else {
      this.mediaData = '';
    }
  }

  /* istanbul ignore next */
  async resolveFileSystemURL(url: string) {
    return new Promise((resolve, reject) => (window as any).resolveLocalFileSystemURL(url, resolve, reject)).catch(e => {});
  }

  async moveFile(filePath) {
    const fileEntry = (await this.resolveFileSystemURL(filePath)) as FileEntry;
    let newPath = '';
    const newFileUri = this.filePlugin.dataDirectory;

    await this.filePlugin.resolveDirectoryUrl(newFileUri).then(dirEntry => {
      newPath = dirEntry.nativeURL + fileEntry.name;
      fileEntry.moveTo(
        dirEntry,
        fileEntry.name,
        /* istanbul ignore next */
        _success => {},
        /* istanbul ignore next */
        _fail => {
          newPath = '';
        }
      );
    });
    return newPath;
  }

  async removeFile(filePath) {
    if (this.platform.is('cordova')) {
      // TODO: investigate this! filePath is File but resolveFileSystemURL requires string
      const fileEntry = (await this.resolveFileSystemURL(filePath)) as FileEntry;
      fileEntry.remove(
        /* istanbul ignore next */
        () => {},
        /* istanbul ignore next */
        error => {}
      );
    } else {
      if (filePath && filePath.fullPath) {
        this.objectURLsvc.revokeObjectURL(filePath.fullPath);
      }
    }
  }

  async retakeOrReupload(question: Question, type: retakeType = 'retake') {
    let headerKey = question.isVideo ? 'retakeRecordingQuestion' : 'retakePhotoQuestion';
    if (type === 'reupload') {
      headerKey = question.isVideo ? 'uploadNewRecordingQuestion' : 'uploadNewPhotoQuestion';
    }
    const messageKey = question.isVideo ? 'retakeMessageVideo' : 'retakeMessagePhoto';
    let retakeKey = question.isVideo ? 'yesRetakeVideo' : 'yesRetakePhoto';
    if (question.hasInstructions) {
      retakeKey = type === 'reupload' ? 'viewInstructionsAndUpload' : 'viewInstructionsAndRetake';
    } else if (type === 'reupload') {
      retakeKey = 'yesUploadAnotherFile';
    }
    const alert = await this.alertController.create({
      header: this.translate.instant('PhotoVideoPage.' + headerKey),
      message: this.translate.instant('Html5CanvasPage.' + messageKey),
      buttons: [
        {
          text: this.translate.instant('PhotoVideoPage.' + retakeKey),
          role: 'retake',
          handler: () => {
            this.removeFile(question.videoPhotoResult);
            question.videoPhotoResult = null;
            question.uploaded = false;
            this.setMediaObject(question);
            question.isValid = -1;
            this.videoPhotoResultChanged.next(question);
            if (question.hasInstructions) {
              this.showInstructionModal(question);
            } else {
              if (type === 'reupload') {
                this.showUploadModal(question, UploadModalType.manual);
              } else {
                this.openCameraPlugin(question);
              }
            }
          },
        },
        {
          text: this.translate.instant('Html5CanvasPage.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        },
      ],
      backdropDismiss: true,
    });
    await alert.present();
  }

  async showInstructionModal(question: Question) {
    const modal = await this.modalController.create({
      component: ViewInstructionsPage,
      componentProps: {
        question,
      },
      cssClass: 'wg-modal view-instructions-modal',
      swipeToClose: true,
      backdropDismiss: true,
    });
    modal.onDidDismiss().then(result => {
      if (result.data?.cameraOpened) {
        question.isValid = -1;
      }
    });
    return await modal.present();
  }

  showPermissionDeniedAlert(): void {
    this.alertSvc.presentAlert(
      this.translate.instant('PhotoVideoPage.permissionDenyTitle'),
      '',
      this.translate.instant('PhotoVideoPage.permissionDenyMessageA') +
        ' ' +
        TENANT_NAME +
        ' ' +
        this.translate.instant('PhotoVideoPage.permissionDenyMessageB'),
      this.translate.instant('QuestionTooltipModal.ok'),
      'camera-alert-ctrl'
    );
  }

  async convertBlobToFile(fileName: string, fileType: string, blob: Blob, imageBlob: Blob = null, uuidFilename = null): Promise<File> {
    if (!uuidFilename) {
      uuidFilename = uuidv4();
    }
    const file: File = { name: uuidFilename + '-' + fileName, type: fileType, fullPath: '', logo: '', size: blob.size };

    const fileEntry = await blobToFile(file.name, blob, this.objectURLsvc);
    file.fullPath = fileEntry.toURL();
    if (imageBlob) {
      const imageFileEntry = await blobToFile(uuidFilename + '-logo.jpeg', imageBlob, this.objectURLsvc);
      file.logo = imageFileEntry.toURL();
    }
    if (this.platform.is('cordova')) {
      await this.moveFile(file.fullPath).then(path => {
        file.fullPath = path;
      });
    }
    return file;
  }

  private isIosBrowser(): boolean {
    return this.platform.is('ios') && this.platform.is('mobileweb');
  }

  toggleVerifyQuestion(questionId: number, verified: boolean) {
    if (verified) {
      this.unverifiedQuestionIds = this.unverifiedQuestionIds.filter(v => v !== questionId);
    } else {
      this.unverifiedQuestionIds.push(questionId);
    }
  }
}

export enum UploadModalType {
  fallback = 'fallback',
  manual = 'manual',
}
